import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { IconButton } from './ui'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  /** Dold rubriktext när titeln redan syns i innehållet. */
  hideTitle?: boolean
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
}

/**
 * Dialogruta som är ett svepbart ark längst ned på mobilen och en centrerad
 * ruta på desktop. Bygger på <dialog> så att fokusfällan, Esc och
 * bakgrundsspärren sköts av webbläsaren i stället för av oss.
 */
export function Modal({
  open,
  onClose,
  title,
  hideTitle,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  /*
   * Två saker som ser ut som en: öppna rutan, och låsa sidan bakom.
   *
   * De satt tidigare i samma gren, `open && !dialog.open`, och låsningen
   * tappades därför bort. React kör effekter dubbelt i utvecklingsläge –
   * effekt, städning, effekt – och andra varvet är rutan redan öppen, så
   * grenen hoppades över medan städningen hunnit låsa upp. Resultatet var en
   * rullningslist för sidan bakom och en för rutan, samtidigt.
   *
   * Nu ställs frågan rätt: ska sidan vara låst, inte är rutan redan öppen.
   * `showModal` är det enda som behöver veta om det redan skett, eftersom den
   * kastar på en ruta som redan är öppen.
   */
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (!open) {
      if (dialog.open) dialog.close()
      document.body.style.overflow = ''
      return
    }

    if (!dialog.open) dialog.showModal()
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        // Klick utanför rutan stänger. Träffar bara när själva <dialog>
        // (bakgrunden) klickas, inte innehållet.
        if (event.target === ref.current) onClose()
      }}
      aria-label={title}
      className={cn(
        'w-full max-w-none bg-transparent p-0 text-ink backdrop:bg-black/50 backdrop:backdrop-blur-sm',
        'm-0 mt-auto max-h-dvh sm:m-auto',
        /*
         * Webbläsarens egen stilmall ger <dialog> overflow: auto, vilket gör
         * den till en andra rullare utanpå kortets egen. Det syns medan
         * öppningsanimationen förskjuter kortet nedåt: en transform räknas in
         * i rullytan, så rutan får en list i en kvarts sekund vid varje
         * öppning. Kortet sköter sin egen rullning och den här ska aldrig
         * rulla.
         */
        'overflow-hidden',
        SIZES[size],
      )}
    >
      <div
        className={cn(
          'flex max-h-[90dvh] flex-col overflow-hidden border border-border bg-surface shadow-lift',
          'rounded-t-3xl sm:rounded-card',
          'motion-safe:animate-[sheet-up_260ms_var(--ease-spring)]',
        )}
      >
        {/* Dragindikator – signalerar att arket hör hemma längst ned på mobilen. */}
        <div className="mx-auto mt-2 h-1 w-10 rounded-pill bg-border-strong sm:hidden" aria-hidden />

        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <h2
            className={cn(
              'font-display text-xl font-extrabold text-balance',
              hideTitle && 'sr-only',
            )}
          >
            {title}
          </h2>
          <IconButton label="Stäng" onClick={onClose} className="-mt-1 -mr-2">
            <X size={22} aria-hidden />
          </IconButton>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-5">{children}</div>

        {footer && (
          <div className="border-t border-border bg-surface-2 px-5 py-4 pb-safe">{footer}</div>
        )}
      </div>
    </dialog>
  )
}
